/* eslint-disable arrow-body-style */
/* eslint-disable camelcase */
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { Container, Grid } from '@material-ui/core';
import { WelcomeBanner, NewsSlider } from '../components/rack/dashboard';
import RacksTable from '../components/rack/dashboard/RacksTable';
import Page from '../components/Page';
import DismissableAlert from '../components/common/DismissibleAlert';
import ExpandableList from '../components/common/ExpandableList';
import { getIDToken } from '../components/authentication/login/amplify';
import { secondsToDateStr } from '../utils/formatTime';
import { DISPLAY_TIME_TO_DELETION_DURATION } from '../utils/constants';

export default function Racks() {
  const [rackList, setRackList] = useState([]);
  const identity = useSelector((state) => state.identity.value);
  const [racksForDeletion, setRacksForDeletion] = useState([]);
  // Threshold (in seconds) below which a rack's pending deletion is surfaced in the
  // list banner — 1209600s = ~14 days. Racks deleting further out are ignored so the
  // banner only warns about imminent deletions.

  // Builds the list of racks slated for inactivity deletion. A rack qualifies when:
  //   - time_till_delete is set (not null), and
  //   - deletion is within the display window (time_till_delete < DISPLAY_TIME_TO_DELETION_DURATION).
  // deleteDate is the scheduled date when time_till_delete > 0, or 'scheduled for deletion'
  // once the deletion deadline itself has passed.
  const determineRacksForDeletion = (rackList) => {
    const deleteRackslist = [];
    rackList.forEach((rack) => {
      const inactivity = rack.lifecycle?.inactivity_policy;
      if (!inactivity) {
        return;
      }
      if (
        inactivity.time_till_delete != null &&
        inactivity.time_till_delete < DISPLAY_TIME_TO_DELETION_DURATION
      ) {
        const deleteDate =
          inactivity.time_till_delete > 0
            ? secondsToDateStr(inactivity.time_till_delete)
            : 'scheduled for deletion';
        deleteRackslist.push({ name: rack.name, deleteDate });
      }
    });
    setRacksForDeletion(deleteRackslist);
  };

  useEffect(() => {
    // initial call to get racklist when identity is updated
    fetchRacks(identity);
    // periodic call to refresh racklist
    const interval = setInterval(() => {
      fetchRacks(identity);
    }, 10000);
    return () => clearInterval(interval);
  }, [identity]);

  useEffect(() => {
    determineRacksForDeletion(rackList);
  }, [rackList]);

  const fetchRacks = async (identity) => {
    const jwt = await getIDToken();
    if (jwt && identity !== '') {
      axios
        .get(`${process.env.REACT_APP_API}/racks/identity/${identity}`, {
          headers: {
            Authorization: `Bearer ${jwt}`
          }
        })
        .then((response) => {
          setRackList(response.data);
          return response.data;
        })
        .catch((error) => {
          console.error('failed to get list of racks', error.message);
        });
    }
  };
  return (
    <Page title="Racks | Virtual Racks">
      <Container maxWidth={false}>
        <Grid container spacing={3} pb={5}>
          <Grid item xs={12}>
            {racksForDeletion.length > 0 && (
              <DismissableAlert severity="warning" variant="filled">
                The following Virtual Racks will be deleted on the date shown due to inactivity.
                <ExpandableList items={racksForDeletion} getKey={(rack) => rack.name} />
                <br />
                To prevent deletion of your Virtual Rack power on your Virtual Rack or contact a
                Virtual Rack admin to extend your lifecycle policy
              </DismissableAlert>
            )}
          </Grid>
          <Grid container item xs={12} spacing={3}>
            <Grid item xs={12} sm={12} md={8}>
              <WelcomeBanner />
            </Grid>
            <Grid item xs={12} sm={12} md={4}>
              <NewsSlider />
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <RacksTable rackList={rackList} />
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
}
